import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import MainLayout from '../components/MainLayout';
import Post, { PostProps } from '../components/Post';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';
import { LoadingSpinner } from '@/components/ui/loading-spinner';

const PostDetail = () => {
  const { postId } = useParams<{ postId: string }>();
  const navigate = useNavigate();
  const [post, setPost] = useState<PostProps | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchPost = async () => {
      if (!postId) {
        setError('No post ID provided');
        setLoading(false);
        return;
      }

      try {
        const { data, error: fetchError } = await supabase
          .from('posts')
          .select(`
            *,
            profiles:user_id(id, username, display_name, avatar_url, level, experience),
            post_images(id, image_url, display_order),
            post_tools(tool)
          `)
          .eq('id', postId)
          .single();

        if (fetchError) {
          console.error('Error fetching post:', fetchError);
          setError('Could not load post. It may have been deleted or is not available.');
          setLoading(false);
          return;
        }

        if (!data) {
          setError('Post not found');
          setLoading(false);
          return;
        }

        // Format post data to match PostProps interface
        const formattedPost: PostProps = {
          id: data.id,
          user: {
            id: data.user_id,
            name: data.profiles?.display_name || 'User',
            username: data.profiles?.username || 'user',
            avatar_url: data.profiles?.avatar_url || '/placeholder.svg',
            level: data.profiles?.level || 1,
            experience: data.profiles?.experience || 0,
          },
          content: data.content,
          created_at: data.created_at,
          likes: data.likes_count || 0,
          upvotes: data.upvotes_count || 0,
          comments: data.comments_count || 0,
          shares: data.shares_count || 0,
          images: data.post_images?.map(img => ({
            image_url: img.image_url.startsWith('http') 
              ? img.image_url 
              : `https://ibuwhrbyjyqrntgwjzsu.supabase.co/storage/v1/object/public/${img.image_url}`
          })),
          link_url: data.link_url,
          tools: data.post_tools?.map(tool => tool.tool) || [],
          openForCollaboration: data.open_for_collaboration,
          tags: [], // Would need to fetch tags separately if needed
        };

        setPost(formattedPost);
        setLoading(false);
      } catch (err) {
        console.error('Error in PostDetail:', err);
        setError('An unexpected error occurred');
        setLoading(false);
      }
    };

    fetchPost();
  }, [postId]);

  const handlePostDeleted = () => {
    toast({
      title: "Post deleted",
      description: "Redirecting to home page..."
    });
    // Redirect to home page after post deletion
    navigate('/home');
  };

  return (
    <MainLayout>
      <div className="max-w-3xl mx-auto px-4 py-6">
        {loading ? (
          <div className="flex justify-center items-center py-20">
            <LoadingSpinner text="Loading post..." />
          </div>
        ) : error ? (
          <div className="text-center py-10">
            <h2 className="text-xl font-bold text-vibebops-text mb-2">Error</h2>
            <p className="text-vibebops-textMuted mb-4">{error}</p>
            <button 
              onClick={() => navigate('/home')} 
              className="px-4 py-2 bg-vibebops-accent text-white rounded-md hover:bg-vibebops-accent/90"
            >
              Go to Home
            </button>
          </div>
        ) : post ? (
          <Post post={post} onPostDeleted={handlePostDeleted} />
        ) : (
          <div className="text-center py-10">
            <h2 className="text-xl font-bold text-vibebops-text mb-2">Post not found</h2>
            <p className="text-vibebops-textMuted mb-4">The post you're looking for doesn't exist or has been removed.</p>
            <button 
              onClick={() => navigate('/home')} 
              className="px-4 py-2 bg-vibebops-accent text-white rounded-md hover:bg-vibebops-accent/90"
            >
              Go to Home
            </button>
          </div>
        )}
      </div>
    </MainLayout>
  );
};

export default PostDetail; 